import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSpring, animated } from '@react-spring/web';
import { ReactSVG } from 'react-svg';
import { Modal } from 'semantic-ui-react';
import Vimeo from "@u-wave/react-vimeo";
import signPost from '../../assets/sign_post.svg';
import trinityLogo from '../../assets/trinity_logo.svg';
import trinityThumbnail from '../../assets/trinity_thumbnail.png';
import background from '../../assets/backgrounds/diagonal_stripes.svg';
import '../Slides.scss';
import './Trinity.scss';

const stages = [
  { key: 1, label: 'Pre-Initial' },
  { key: 2, label: 'Initial' },
  { key: 3, label: 'Grade 1' },
  { key: 4, label: 'Grade 2' }
];

const TrinityGradeSlide = ({ data, triggerAudioTrackPause, isAndroid }) => {
  const {
    current_trinity_stage,
    stage_progress_percent = 0
  } = data;

  const darkOrange = '#EC660D';
  const yellow = '#FFEF77';
  const lightOrange = '#F8AB44';

  const [fillStages, setFillStages] = useState({});
  const [animationStep, setAnimationStep] = useState(0);
  const [finalPhase, setFinalPhase] = useState(false);
  const [currentStageFill, setCurrentStageFill] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);

  const updateStageColors = (activeStage) => {
    const updated = {};
    stages.forEach(stage => {
      if (stage.key <= activeStage && activeStage > 0) {
        updated[stage.key] = 100;
      } else {
        updated[stage.key] = 0;
      }
    });
    setFillStages(updated);
  };

  const signPostSpring = useSpring({
    from: { opacity: 0, transform: 'scale(0)' },
    to: { opacity: 1, transform: 'scale(1)' },
    delay: 2000,
    config: {
      tension: 180,
      friction: 12,
    },
  })

  useEffect(() => {
    const baseSteps = [
      () => updateStageColors(1),
      () => updateStageColors(2),
      () => updateStageColors(3),
      () => updateStageColors(4),
      () => updateStageColors(3),
      () => updateStageColors(2),
      () => updateStageColors(1),
      () => updateStageColors(0),
    ];

    const refillSteps = [];
    for (let i = 1; i < current_trinity_stage; i += 1) {
      refillSteps.push(() => updateStageColors(i));
    }

    const steps = [...baseSteps, ...refillSteps, () => setFinalPhase(true)];


    const stepDurations = [
      1000, // stage 1 fill
      100, // stage 2 fill
      100, // stage 3 fill
      100, // stage 4 fill
      100, // stage 4 un-fill
      100, // stage 3 un-fill
      100, // stage 2 un-fill
      100, // stage 1 un-fill
      ...Array(refillSteps.length).fill(100),
      100 // pause before final phase
    ];

    if (!finalPhase && animationStep < steps.length) {
      const timeout = setTimeout(() => {
        steps[animationStep]();
        setAnimationStep(prev => prev + 1);
      }, stepDurations[animationStep]);
  
      return () => clearTimeout(timeout);
    }
  
    return undefined;
  }, [animationStep, finalPhase]);

  useEffect(() => {
    if (finalPhase && current_trinity_stage && stage_progress_percent > 0) {
      let progress = 0;
  
      const interval = setInterval(() => {
        progress += 1;
        if (progress > stage_progress_percent) {
          clearInterval(interval);
        } else {
          setCurrentStageFill(progress);
        }
      }, 20);
  
      return () => clearInterval(interval);
    }
    return undefined;
  }, [finalPhase, current_trinity_stage, stage_progress_percent]);

  const renderStage = (stage, springStyle) => {
    let fillPercent = 0;
    let filledColor = yellow;
    let unfilledColor = yellow;
    const isActive = stage.key === current_trinity_stage;

    if (!finalPhase) {
      fillPercent = fillStages[stage.key] ?? 0;
      if (fillPercent === 100) {
        filledColor = darkOrange;
        unfilledColor = darkOrange;
      } else {
        filledColor = yellow;
        unfilledColor = yellow;
      }
    } else if (stage.key < current_trinity_stage) {
      fillPercent = 100;
      filledColor = darkOrange;
      unfilledColor = darkOrange;
    } else if (stage.key === current_trinity_stage) {
      fillPercent = currentStageFill;
      filledColor = darkOrange;
      unfilledColor = lightOrange;
    } else {
      fillPercent = 0;
      filledColor = yellow;
      unfilledColor = yellow;
    }

    return (
      <animated.div
        key={stage.key}
        className='Slide__trinity_grade__board__wrapper'
      >
        <animated.h2
          className={`Slide__trinity_grade__board__item ${isActive ? 'Slide__trinity_grade__board__item--active' : ''}`}
          style={{
            ...springStyle,
            color: stage.key < current_trinity_stage || stage.key === current_trinity_stage ? 'white' : '#2A2B33',
            background: `linear-gradient(to right, ${filledColor} ${fillPercent}%, ${unfilledColor} ${fillPercent}%)`,
          }}
        >
          {stage.label}
        </animated.h2>
        {isActive && (
          <animated.div className='Slide__trinity_grade__board__signPost' style={signPostSpring}>
            <ReactSVG src={signPost} />
          </animated.div>
          )}
      </animated.div>
    );
  };

  const trinityLogoSpring = useSpring({ 
    delay: 400, 
    from: { opacity: 0, transform: 'translateY(20px)' }, 
    to: { opacity: 1, transform: 'translateY(0px)' } 
  });

  const titleSpring = useSpring({
    delay: 400,
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' }
  });

  const boardSpring = useSpring({
    delay: 400,
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' }
  });

  const boxSpring = useSpring({
    delay: 300,
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: { opacity: 1, transform: 'translateY(0px)' }
  });

  const videoSpring = useSpring({
    delay: 1200,
    from: { opacity: 0, transform: 'scale(0.8)' },
    to: { opacity: 1, transform: 'scale(1)' }
  });

  const stageSprings = [
    useSpring({ delay: 200, from: { opacity: 0, transform: 'scale(0.8)' }, to: { opacity: 1, transform: 'scale(1)' } }),
    useSpring({ delay: 200, from: { opacity: 0, transform: 'scale(0.8)' }, to: { opacity: 1, transform: 'scale(1)' } }),
    useSpring({ delay: 200, from: { opacity: 0, transform: 'translateY(20px)' }, to: { opacity: 1, transform: 'translateY(0px)' } }),
    useSpring({ delay: 200, from: { opacity: 0, transform: 'translateY(20px)' }, to: { opacity: 1, transform: 'translateY(0px)' } }),
  ];

    const appear = useSpring({
      from: { opacity: 0 },
      to: { opacity: 1 },
      delay: 1000,
    })
  
    const backgroundScale = useSpring({
      loop: { reverse: true },
      from: { transform: 'scale(1)' },
      to: { transform: 'scale(1.1)' },
      config: { duration: 8000 },
    });
  
    const backgroundSpring = {
      ...appear,
      ...backgroundScale
    }

    const openVideo = () => {
      triggerAudioTrackPause(true);
      setVideoOpen(true)
    }

    const closeVideo = () => {
      triggerAudioTrackPause(false);
      setVideoOpen(false)
    }

  return (
    <div className='Slide__trinity_grade'>
      <animated.div className='Slide__trinity_grade__logo' style={trinityLogoSpring}>
        <ReactSVG src={trinityLogo} />
      </animated.div>
      <animated.h1 className='Slide__trinity_grade__title' style={titleSpring}>
        Exam-free music performance in bands qualifications
      </animated.h1>
      <animated.div className='Slide__trinity_grade__board' style={boardSpring}>
        <div className='Slide__trinity_grade__board__container'>
          {stages.slice().reverse().map((stage, idx) => renderStage(stage, stageSprings[idx]))}
        </div>
      </animated.div>
      <animated.div
        className='Slide__trinity_grade__box'
        style={{
          ...boxSpring,
          paddingBottom: isAndroid ? '70px' : '20px'
        }}
      >
        <animated.button
          className='Slide__trinity_grade__box__thumbnail'
          style={videoSpring}
          onClick={() => openVideo()}
        >
          <img alt='trinity_thumbnail' src={trinityThumbnail} />
        </animated.button>
        <animated.button
          className='Slide__trinity_grade__box__subTitle'
          style={videoSpring}
          onClick={() => openVideo()}
        >
          Play video
        </animated.button>
      </animated.div>
      <animated.div className='Slide__trinity_grade__background' style={backgroundSpring}>
        <ReactSVG src={background} />
      </animated.div>
      <Modal open={videoOpen} onClose={() => closeVideo()}>
        <Vimeo
          video='https://vimeo.com/870571499/f1690e22e2'
          autoplay={false}
          muted={false}
          responsive
          controls
        />
      </Modal>
    </div>
  );
};

TrinityGradeSlide.propTypes = {
  data: PropTypes.shape({
    current_trinity_stage: PropTypes.number.isRequired,
    stage_progress_percent: PropTypes.number,
  }).isRequired,
  triggerAudioTrackPause: PropTypes.func.isRequired,
  isAndroid: PropTypes.bool
};

TrinityGradeSlide.defaultProps = {
  isAndroid: false
}

export default TrinityGradeSlide;
